import { useState, useEffect, useMemo } from 'react';
import { X, Loader2, Flame, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { trackEvent, submitLead } from '@/utils/marketplaceEvents';
import { toast } from 'sonner';

/**
 * QuickLeadModal — v2 P0 marketplace.
 *
 * Pojawia się po kliknięciu jednego głównego CTA na karcie ofert.
 * Pola: imię + telefon + wiadomość (z prefillem) + email opcjonalny.
 * Submit → POST /api/public/leads + event lead_submit + ekran sukcesu z informacją o czasie odpowiedzi.
 */

function buildPrefillMessage(item) {
  if (!item) return '';
  const breed = (item.breed || '').trim();
  if (item.type === 'reproduktor') {
    return breed
      ? `Dzień dobry, czy reproduktor ${item.title || ''} (${breed}) jest dostępny do krycia? Proszę o kontakt.`
      : `Dzień dobry, czy reproduktor ${item.title || ''} jest dostępny do krycia? Proszę o kontakt.`;
  }
  if (item.type === 'hodowla') {
    return breed
      ? `Dzień dobry, czy planują Państwo miot rasy ${breed}? Proszę o kontakt.`
      : `Dzień dobry, interesuje mnie hodowla ${item.title || ''}. Proszę o kontakt.`;
  }
  // miot (default)
  return breed
    ? `Dzień dobry, czy szczenięta rasy ${breed} z miotu „${item.title || ''}" są jeszcze dostępne?`
    : `Dzień dobry, czy szczenięta z miotu „${item.title || ''}" są jeszcze dostępne?`;
}

export default function QuickLeadModal({ open, onClose, item }) {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const prefill = useMemo(() => buildPrefillMessage(item), [item]);

  useEffect(() => {
    if (open) {
      setName('');
      setPhone('');
      setEmail('');
      setMessage(prefill);
      setSubmitting(false);
      setSent(false);
    }
  }, [open, prefill]);

  if (!open || !item) return null;

  const ctaLabel = item.type === 'hodowla'
    ? 'Skontaktuj się'
    : item.type === 'reproduktor'
      ? 'Zapytaj o krycie'
      : '🔥 Zapytaj o szczenięta';

  const entityType = item.type === 'hodowla'
    ? 'kennel'
    : item.type === 'reproduktor'
      ? 'stud'
      : 'litter';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !phone.trim() || submitting) return;

    setSubmitting(true);
    try {
      const result = await submitLead({
        entityType,
        entityId: item.id || item.slug,
        name: name.trim(),
        phone: phone.trim(),
        email: email.trim() || undefined,
        message: message.trim() || `Interesuje mnie: ${item.title}`,
        source: 'marketplace_quick_lead',
      });

      if (result.ok) {
        trackEvent('lead_submit', { entity_type: entityType, entity_id: item.id || item.slug });
        toast.success('Zapytanie wysłane', {
          description: 'Hodowca odpowiada zwykle w ciągu kilku godzin.'
        });
        setSent(true);
      } else {
        toast.error('Coś poszło nie tak', { description: 'Spróbuj ponownie.' });
      }
    } catch (err) {
      toast.error('Coś poszło nie tak', { description: 'Spróbuj ponownie.' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/75 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="quick-lead-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-3xl border border-white/10 bg-zinc-950 p-6 shadow-2xl text-white"
      >
        <div className="flex items-start justify-between gap-3 mb-5">
          <div>
            <div className="text-xs uppercase tracking-[0.18em] text-orange-400 mb-1">
              {sent ? 'Gotowe' : 'Szybkie zapytanie'}
            </div>
            <div className="text-xl font-semibold leading-tight">
              {sent ? 'Zapytanie wysłane' : ctaLabel}
            </div>
            <div className="mt-1 text-sm text-white/55 line-clamp-1">{item.title}</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-1.5 text-white/60 hover:bg-white/10 hover:text-white transition"
            aria-label="Zamknij"
            data-testid="quick-lead-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {sent ? (
          <div data-testid="quick-lead-success" className="py-4">
            <div className="flex items-start gap-3 rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-4 mb-4">
              <CheckCircle2 className="w-6 h-6 text-emerald-400 shrink-0 mt-0.5" />
              <div>
                <div className="text-sm font-semibold text-emerald-200">
                  Zapytanie wysłane
                </div>
                <div className="mt-1 text-sm text-white/75">
                  Hodowca odpowiada zwykle w <span className="font-semibold text-white">ciągu kilku godzin</span>.
                </div>
              </div>
            </div>
            {item.whatsapp_url && (
              <a
                href={item.whatsapp_url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackEvent('whatsapp_click', { source: 'lead_success', entity_id: item.id || item.slug })}
                data-testid="quick-lead-whatsapp-boost"
                className="flex items-center justify-between gap-3 rounded-2xl border border-emerald-500/40 bg-emerald-500/5 p-4 mb-4 hover:bg-emerald-500/10 transition"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center shrink-0">
                    <span className="text-emerald-300 text-xl">💬</span>
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-white">Napisz na WhatsApp</div>
                    <div className="text-[12px] text-white/60">…żeby przyspieszyć odpowiedź</div>
                  </div>
                </div>
                <span className="text-emerald-300">→</span>
              </a>
            )}
            <Button
              type="button"
              onClick={onClose}
              data-testid="quick-lead-done"
              className="w-full rounded-xl bg-orange-500 hover:bg-orange-400 text-black font-semibold h-11"
            >
              Przeglądaj dalej
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <label className="block mb-3">
              <span className="block mb-1 text-xs text-white/60">Imię</span>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                autoFocus
                placeholder="Jan Kowalski"
                data-testid="quick-lead-name"
                className="w-full rounded-xl bg-white/[0.06] border border-white/10 px-4 py-3 text-white placeholder:text-white/30 focus:outline-none focus:border-orange-400/50 focus:bg-white/[0.08]"
              />
            </label>

            <label className="block mb-3">
              <span className="block mb-1 text-xs text-white/60">Telefon / WhatsApp</span>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                required
                placeholder="+48 600 000 000"
                data-testid="quick-lead-phone"
                className="w-full rounded-xl bg-white/[0.06] border border-white/10 px-4 py-3 text-white placeholder:text-white/30 focus:outline-none focus:border-orange-400/50 focus:bg-white/[0.08]"
              />
            </label>

            <label className="block mb-3">
              <span className="block mb-1 text-xs text-white/60">Twoja wiadomość</span>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={3}
                placeholder="Napisz kilka słów do hodowcy"
                data-testid="quick-lead-message"
                className="w-full rounded-xl bg-white/[0.06] border border-white/10 px-4 py-3 text-white placeholder:text-white/30 focus:outline-none focus:border-orange-400/50 focus:bg-white/[0.08] resize-none"
              />
              <span className="block mt-1 text-[10px] text-white/40">
                Wiadomość jest wypełniona automatycznie — możesz ją edytować.
              </span>
            </label>

            <label className="block mb-5">
              <span className="block mb-1 text-xs text-white/45">E-mail <span className="text-white/35">(opcjonalnie)</span></span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="adres@email.pl"
                data-testid="quick-lead-email"
                className="w-full rounded-xl bg-white/[0.04] border border-white/10 px-4 py-3 text-white placeholder:text-white/30 focus:outline-none focus:border-white/30 focus:bg-white/[0.06]"
              />
            </label>

            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={submitting}
                data-testid="quick-lead-cancel"
                className="flex-1 rounded-xl border-white/15 bg-white/[0.04] text-white hover:bg-white/10"
              >
                Anuluj
              </Button>
              <Button
                type="submit"
                disabled={submitting || !name.trim() || !phone.trim()}
                data-testid="quick-lead-submit"
                className="flex-1 rounded-xl bg-orange-500 hover:bg-orange-400 text-black font-semibold"
              >
                {submitting ? (
                  <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Wysyłanie…</>
                ) : (
                  <><Flame className="w-4 h-4 mr-1.5" /> Wyślij zapytanie</>
                )}
              </Button>
            </div>

            <p className="mt-3 text-[11px] text-white/40 text-center">
              Odpowiedź zwykle w <span className="text-white/70 font-semibold">kilka godzin</span>. Wysyłając akceptujesz{' '}
              <a href="/regulamin" className="underline hover:text-white/70">regulamin</a> i{' '}
              <a href="/polityka-prywatnosci" className="underline hover:text-white/70">politykę prywatności</a>.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
